import { IconProps } from '@euler/app/components/icons/types';
import { Typography } from '@euler/app/components/typography';
import { StatusColors } from '@euler/components/Colors';
import { IconType } from '@euler/components/Icomoon';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { getValidateErrorMessage } from '@euler/lib/form/controller';
import { SiteInspectionType } from '@euler/model/enum';
import { TaskMediaStaged } from '@euler/model/task-detail';
import { showAlert } from '@euler/utils';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { memo, useMemo } from 'react';
import { View } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useOrderFlowContext } from '../../../../OrderFlowContext';
import { Cell } from '../components/Cell';
import { IconWrapper } from '../components/IconWrapper';
import { Row } from '../components/Row';

export const FacadeInfoRow = memo(
  ({ required, ...props }: { required?: boolean; disabled?: boolean }) => {
    const {
      theme,
      navigation,
      orderDetail$,
      formController: { form },
    } = useOrderFlowContext();

    const { rpx } = useResponsiveContext();

    const { iconColor, placeholderColor } = theme;

    const [orderDetail] = useBehaviorSubject(orderDetail$);

    const inspectedSiteCount = useMemo(() => {
      return (
        orderDetail.preInspection?.filter(
          x => x.inspectionType === SiteInspectionType.Facade,
        ).length ?? 0
      );
    }, [orderDetail]);

    const medias = useMemo(() => {
      const inspectedSites =
        orderDetail.preInspection?.filter(
          x => x.inspectionType === SiteInspectionType.Facade,
        ) ?? [];
      return inspectedSites.flatMap(x => x.items).flatMap(x => x.medias);
    }, [orderDetail]);

    const helpText = getHelpTextInfo(
      medias,
      required,
      form.facadeInspectionResults.error
        ? getValidateErrorMessage(form.facadeInspectionResults.error)
        : undefined,
    );

    const onPress = usePersistFn(async () => {
      if (!form.licensePlateNo.value) {
        await showAlert(
          '提示',
          '外观拍照图片标注需要车牌信息，请先填写或识别车牌!',
        );
        return;
      }
      navigation.navigate('_facadeInspection', {});
    });

    return (
      <Row
        errorProp="facadeInspectionResults"
        icon={
          <IconWrapper>
            <RowIcon color={iconColor} />
          </IconWrapper>
        }
        detailIcon="disclosure"
        onPress={onPress}
        {...props}
      >
        <View
          css={`
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
          `}
        >
          <Cell
            required={required}
            label="外观/物品记录"
            helpText={helpText.text}
            helpTextIcon={helpText.icon}
            helpTextIconColor={helpText.color}
            helpTextStyle={{
              marginTop: rpx(4),
              color: helpText.color,
              top: rpx(-1.4),
              lineHeight: rpx(14),
            }}
            css={`
              flex: 1;
            `}
          >
            <Typography.Label
              color={
                medias.length > 0 ? StatusColors.Success : placeholderColor
              }
              family={medias.length > 0 ? 'medium' : 'light'}
              size={rpx(16)}
            >
              {`${inspectedSiteCount}部位, ${medias.length}图片/视频记录`}
            </Typography.Label>
          </Cell>
        </View>
      </Row>
    );
  },
);

const kFakeUploadingCount = 0;
const kFakeErrorCount = 0;

function getHelpTextInfo(
  medias: TaskMediaStaged[],
  required: boolean | undefined,
  errorMsg: string | undefined,
) {
  const uploadingCount =
    kFakeUploadingCount ||
    medias.filter(
      x =>
        x._source.type === 'local' &&
        x._source.state.status !== 'error' &&
        x._source.state.status !== 'uploaded',
    ).length;
  const errorCount =
    kFakeErrorCount ||
    medias.filter(
      x => x._source.type === 'local' && x._source.state.status === 'error',
    ).length;

  let icon: IconType | 'spinner' | undefined = undefined;
  let text: string | undefined = undefined;
  let color: string | undefined = undefined;

  if (uploadingCount > 0) {
    icon = 'spinner';
    text = `正在上传请稍候, ${uploadingCount}剩余...`;
    color = StatusColors.Info;
  } else if (errorCount > 0) {
    icon = 'error';
    text = `${errorCount}图片/视频上传失败`;
    color = StatusColors.Danger;
  } else if (medias.length === 0 && required !== false) {
    color = errorMsg ? StatusColors.Danger : StatusColors.Warn;
    text =
      errorMsg ??
      `${required ? '需' : '建议'}进行车辆外观检查和相关物品记录拍照`;
    icon = errorMsg ? 'error' : undefined;
  }

  return { icon, text, color };
}

const RowIcon = memo(
  ({ size, style, color = '#0077FF', ...props }: IconProps) => {
    const { rpx } = useResponsiveContext();
    return (
      <Svg
        width={size ?? rpx(25)}
        height={size ?? rpx(15)}
        viewBox="0 0 25.2632 15"
        fill="none"
        style={style}
        {...props}
      >
        <Path
          d="M19.7368 5.52632L19.3105 5.52632L15.5226 1.10684C15.2267 0.759079 14.8585 0.479919 14.4438 0.288773C14.0291 0.097641 13.5778 -0.000900269 13.1211 0L7.68945 0C7.13367 0.00050354 6.58777 0.147705 6.10706 0.426758C5.62622 0.705795 5.22766 1.1068 4.95154 1.58922L2.69995 5.52632L1.57898 5.52632C0.70813 5.52632 0 6.23448 0 7.10527L0 11.8421C0 12.0515 0.0831299 12.2523 0.231201 12.4003C0.379272 12.5484 0.580078 12.6316 0.789429 12.6316L3.27002 12.6316C3.62292 13.9895 4.849 15 6.3158 15C7.78259 15 9.00793 13.9895 9.36157 12.6316L15.9016 12.6316C16.2545 13.9895 17.4805 15 18.9474 15C20.4142 15 21.6394 13.9895 21.9932 12.6316L24.4736 12.6316C24.6831 12.6316 24.8839 12.5484 25.0319 12.4003C25.1799 12.2523 25.2632 12.0515 25.2632 11.8421L25.2632 11.0526C25.2632 8.00526 22.7842 5.52632 19.7368 5.52632ZM11.0526 1.57895L13.1211 1.57895C13.3492 1.57841 13.5747 1.62756 13.782 1.72301C13.9891 1.81844 14.1731 1.95786 14.321 2.13159L17.2311 5.52632L11.0526 5.52632L11.0526 1.57895ZM6.32288 2.37238C6.46069 2.13156 6.65967 1.9314 6.89966 1.79208C7.13953 1.65277 7.41199 1.57925 7.68945 1.57895L9.47363 1.57895L9.47363 5.52632L4.51892 5.52632L6.32288 2.37238ZM6.3158 13.4211C5.44495 13.4211 4.73682 12.7129 4.73682 11.8421C4.73682 10.9713 5.44495 10.2632 6.3158 10.2632C7.18652 10.2632 7.89478 10.9713 7.89478 11.8421C7.89478 12.7129 7.18652 13.4211 6.3158 13.4211ZM18.9474 13.4211C18.0765 13.4211 17.3684 12.7129 17.3684 11.8421C17.3684 10.9713 18.0765 10.2632 18.9474 10.2632C19.8181 10.2632 20.5264 10.9713 20.5264 11.8421C20.5264 12.7129 19.8181 13.4211 18.9474 13.4211ZM21.9932 11.0526C21.6403 9.69473 20.4142 8.68422 18.9474 8.68422C17.4805 8.68422 16.2552 9.69473 15.9016 11.0526L9.36157 11.0526C9.00867 9.69473 7.78259 8.68422 6.3158 8.68422C4.849 8.68422 3.62366 9.69473 3.27002 11.0526L1.57898 11.0526L1.57898 7.10527L19.7368 7.10527C21.9135 7.10527 23.6842 8.87605 23.6842 11.0526L21.9932 11.0526Z"
          fillRule="evenodd"
          fill={color}
        />
      </Svg>
    );
  },
);
